import { useState } from 'react'
import { MessageSquare } from 'lucide-react'
import { toast } from 'sonner'
import {
  useCreateRatingComment,
  useDeleteRatingComment,
  useRatingComments,
  useUpdateRatingComment,
  type RatingComment,
  type RatingThread,
} from '@/api/quality-tiers'
import { CommentComposer, CommentItem, CommentThreadView } from '@/components/comments'
import {
  MANUFACTURER_RATING_KINDS,
  MANUFACTURER_RATINGS,
  type ManufacturerRatingKind,
} from '@/lib/manufacturer-ratings'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'

type RowContext = {
  nodeId: number
  manufacturerUuid: string
  manufacturerName: string
  categoryName: string
  count: number
  /** The rating's editable chip, the same one the row shows. */
  renderRating: (rating: ManufacturerRatingKind) => React.ReactNode
}

/**
 * The comments button on a manufacturer's ratings row (USA-1272): the comment count, opening the
 * three ratings together with one comment thread, the same comments as projects and POs.
 */
export function RatingCommentsButton({ className, count, ...context }: RowContext & { className?: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className={cn('h-8 gap-1 px-2 tabular-nums', count ? 'text-foreground' : 'text-muted-foreground/70', className)}
        aria-label={`Comments on ${context.manufacturerName} in ${context.categoryName}${count ? `, ${count}` : ''}`}
        title="Comments"
        onClick={() => setOpen(true)}
      >
        <MessageSquare className="size-4" aria-hidden="true" />
        {count > 0 && <span className="text-xs">{count}</span>}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
          {open && <RatingCommentsPanel {...context} />}
        </SheetContent>
      </Sheet>
    </>
  )
}

function RatingCommentsPanel({ nodeId, manufacturerUuid, manufacturerName, categoryName, renderRating }: Omit<RowContext, 'count'>) {
  const thread = { nodeId, manufacturerUuid }
  const { data: comments, isLoading, error } = useRatingComments(thread)
  const create = useCreateRatingComment(thread)
  const { user } = useAuth()
  return (
    <>
      <SheetHeader className="space-y-1 border-b p-4 pr-12 text-left">
        <SheetTitle>{manufacturerName}</SheetTitle>
        <SheetDescription>{categoryName}</SheetDescription>
      </SheetHeader>
      <div className="space-y-4 border-b p-4">
        <dl className="grid grid-cols-3 gap-2">
          {MANUFACTURER_RATING_KINDS.map((rating) => (
            <div key={rating} className="min-w-0 space-y-1">
              <dt className="text-xs text-muted-foreground">{MANUFACTURER_RATINGS[rating].label}</dt>
              <dd>{renderRating(rating)}</dd>
            </div>
          ))}
        </dl>
        <CommentComposer
          pending={create.isPending}
          onSubmit={(comment, clear) =>
            create.mutate(comment, {
              onSuccess: clear,
              onError: () => toast.error('Could not save the comment'),
            })
          }
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <CommentThreadView
          comments={comments}
          isLoading={isLoading}
          error={error}
          emptyHint={null}
          renderComment={(comment) => (
            <ThreadComment key={comment.id} comment={comment} thread={thread} currentUserEmail={user?.email} />
          )}
        />
      </div>
    </>
  )
}

function ThreadComment({
  comment,
  thread,
  currentUserEmail,
}: {
  comment: RatingComment
  thread: RatingThread
  currentUserEmail: string | undefined
}) {
  const update = useUpdateRatingComment(thread)
  const remove = useDeleteRatingComment(thread)
  return (
    <CommentItem
      comment={comment}
      currentUserEmail={currentUserEmail}
      saving={update.isPending}
      deleting={remove.isPending}
      onSave={(content, done) =>
        update.mutate({ commentUuid: comment.uuid, content }, { onSuccess: done })
      }
      onDelete={() => remove.mutate(comment.uuid)}
    />
  )
}
